import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// RequireAuth — gate any child route on an authenticated session.
// While the initial /api/auth/session probe is in flight, render a
// neutral loading state so the router doesn't flash the login screen.
// On unauth → redirect to /auth/session-expired (or /auth/login for a
// first visit) preserving the intended destination in `state.from`.
export default function RequireAuth({ children }) {
  const { loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: "#0f0f0f", color: "#9a9a9a" }}
           data-testid="require-auth-loading">
        <div className="text-xs" style={{ letterSpacing: "1px" }}>Loading…</div>
      </div>
    );
  }

  if (!user) {
    // First visit → /auth/login. Session actually expired → /auth/session-expired.
    // We can't reliably tell these apart client-side, so we send returning
    // visitors (indicated by a stored token that got rejected) to
    // session-expired, first-timers to login.
    const hadToken = (() => { try { return !!localStorage.getItem("cdi.auth.token"); } catch { return false; } })();
    const target = hadToken ? "/auth/login" : "/auth/signup";
    return <Navigate to={target} state={{ from: location.pathname + location.search }} replace />;
  }

  return children;
}
